import { createRouter, createWebHistory, RouteLocationNormalized, Router } from 'vue-router'
import { useStore } from 'vuex'

import i18n from './i18n'
import { authentication } from './router/guards/authentication.guard.ts'
import { requireEmailVerification } from './router/guards/requireEmailVerification.guard.ts'
import { isSubscriptionTradeInEnabled } from './shared/subscriptionTransfer'
import { UPGRADE_OFFERS } from './shared/upgradeOffers'
import { RouteGuard } from './types/router.ts'

// route updates :
// /subscriptionSelector route added as child of subscription route
const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Redirect to subscriptions if user is logged in, otherwise show login page
    {
      path: '/',
      name: 'home',
      meta: {
        guards: [authentication],
        title: 'Invoxia - ' + i18n.global.t('home'),
        layout: 'LayoutPortal',
      },
      component: () => import('./pages/Home.vue'),
    },
    // Show shopify orders
    {
      path: '/orders',
      name: 'orders',
      meta: {
        guards: [authentication, requireEmailVerification],
        title: 'Invoxia - ' + i18n.global.t('orders'),
        layout: 'LayoutPortal',
      },
      component: () => import('./pages/OrdersPage.vue'),
    },
    {
      path: '/subscription-invoices',
      name: 'subscriptionInvoices',
      meta: {
        guards: [authentication, requireEmailVerification],
        title: 'Invoxia - ' + i18n.global.t('subscription_invoices'),
        layout: 'LayoutPortal',
      },
      component: () => import('./pages/SubscriptionInvoicesPage.vue'),
    },
    // Partner offer codes: linked from the website partners pages. The backend decides which offers the account is eligible for.
    {
      path: '/partners',
      name: 'partners',
      meta: {
        guards: [authentication, requireEmailVerification],
        title: 'Invoxia - ' + i18n.global.t('partner_offers'),
        layout: 'LayoutPortal',
      },
      component: () => import('./pages/PartnersPage.vue'),
    },
    // Upgrade landing pages, linked from email campaigns: one per offer of UPGRADE_OFFERS, any other
    // slug falls through to the 404. Not registered at all while the trade-in is off (404 too).
    // No requireEmailVerification: the page explains an unconfirmed email instead of bouncing to home.
    ...(isSubscriptionTradeInEnabled()
      ? [
          {
            path: `/upgrade/:offer(${Object.keys(UPGRADE_OFFERS).join('|')})`,
            name: 'upgrade',
            props: (route: RouteLocationNormalized) => ({ offer: UPGRADE_OFFERS[route.params.offer as string] }),
            meta: {
              guards: [authentication],
              title: 'Invoxia - ' + i18n.global.t('upgrade_offer'),
              layout: 'LayoutPortal',
            },
            component: () => import('./pages/UpgradePage.vue'),
          },
        ]
      : []),
    {
      path: '/login',
      name: 'login',
      meta: {
        title: 'Invoxia - ' + i18n.global.t('login_signin'),
        layout: 'LayoutAuthentication',
      },
      component: () => import('./pages/LoginPage.vue'),
    },
    {
      path: '/signup',
      name: 'signup',
      meta: {
        title: 'Invoxia - ' + i18n.global.t('signup'),
        layout: 'LayoutAuthentication',
      },
      component: () => import('./pages/SignupPage.vue'),
    },
    {
      path: '/subscription',
      name: 'subscription',
      meta: {
        guards: [authentication, requireEmailVerification],
        title: 'Invoxia - ' + i18n.global.t('subscriptions'),
        layout: 'LayoutPortal',
      },
      component: () => import('./pages/SubscriptionPage.vue'),
      children: [
        {
          path: ':deviceId',
          name: 'deviceOverview',
          props: true,
          component: () => import('./components/tracker/DeviceOverview.vue'),
        },
        {
          path: ':deviceId/selector',
          name: 'subscriptionPlanSelector',
          props: true,
          component: () => import('./components/subscription/SubscriptionCommonPlanSelector.vue'),
        },
        // Not registered while the trade-in is off: the URL falls through to the 404
        ...(isSubscriptionTradeInEnabled()
          ? [
              {
                path: ':deviceId/transfer',
                name: 'subscriptionTransfer',
                props: true,
                component: () => import('./components/subscription/SubscriptionTransfer.vue'),
              },
            ]
          : []),
        {
          path: ':deviceId/selector/lite-confirmation',
          name: 'liteConfirmation',
          props: true,
          component: () => import('./components/subscription/LiteConfirmation.vue'),
        },
      ],
    },
    // deprecated ?
    // Used to link a Minitailz with its subscription received from mail
    {
      path: '/link_subscription',
      name: 'linkSubscription',
      meta: { title: 'Invoxia - ' + i18n.global.t('linkSubscription') },
      component: () => import('./pages/LinkSubscription.vue'),
    },
    // deprecated ?
    {
      path: '/link_subscription_success',
      name: 'linkSubscriptionSucess',
      meta: { title: 'Invoxia - ' + i18n.global.t('linkSubscriptionSucess') },
      component: () => import('./pages/LinkSubscriptionSucess.vue'),
    },
    {
      path: '/account_email_update',
      name: 'accountEmailUpdate',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountEmailUpdate'), noTransition: true },
      component: () => import('./pages/AccountEmailUpdate.vue'),
    },
    {
      path: '/account_email_update_complete',
      name: 'AccountEmailUpdateComplete',
      meta: {
        title: 'Invoxia - ' + i18n.global.t('AccountEmailUpdateComplete'),
        noTransition: true,
      },
      component: () => import('./pages/AccountEmailUpdateComplete.vue'),
    },
    {
      path: '/account_profil_update',
      name: 'AccountProfilUpdate',
      meta: { title: 'Invoxia - ' + i18n.global.t('AccountProfilUpdate'), noTransition: true },
      component: () => import('./pages/AccountProfilUpdate.vue'),
    },
    {
      path: '/account_confirmation',
      name: 'accountConfirmation',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountConfirmation'), noTransition: true },
      component: () => import('./pages/AccountConfirmation.vue'),
    },
    {
      // SLA: not sure if this page is used, for a deeplink. It is called from backend banner, but in general needs to be replaced by native part.
      // GAU: account_email does the same than below route /account_email_sent
      path: '/account_email',
      name: 'accountEmail',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountEmail'), noTransition: true },
      component: () => import('./pages/AccountEmail.vue'),
    },
    {
      path: '/account_email_sent',
      name: 'accountEmailSent',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountEmail'), noTransition: true },
      component: () => import('./pages/AccountEmail.vue'),
    },
    {
      path: '/account',
      name: 'accountSettings',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountSettings'), noTransition: true },
      component: () => import('./pages/AccountSettings.vue'),
    },
    {
      path: '/account/:pageVersion',
      name: 'accountSettingsVersion',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountSettings'), noTransition: true },
      component: () => import('./pages/AccountSettings.vue'),
    },
    {
      path: '/account_delete',
      name: 'accountDelete',
      meta: { title: 'Invoxia - ' + i18n.global.t('accountDelete'), noTransition: true },
      component: () => import('./pages/AccountDelete.vue'),
    },
    {
      path: '/operators/helium/',
      name: 'heliumOperator',
      meta: { title: 'Invoxia x Helium' },
      component: () => import('./pages/HeliumOperator.vue'),
    },
    {
      path: '/config_sct2',
      name: 'configSCT2',
      meta: {
        title: 'Invoxia config sct2',
        layout: 'LayoutSCT2',
      },
      component: () => import('./pages/NewSCT2Param.vue'),
    },
    {
      path: '/vacation-about',
      name: 'vacationAbout',
      meta: { title: 'Invoxia - ' + i18n.global.t('vacation_mode_title'), noTransition: true },
      component: () => import('./pages/VacationAbout.vue'),
    },
    {
      path: '/vacation-config',
      name: 'vacationConfig',
      meta: { title: 'Invoxia - ' + i18n.global.t('vacation_mode_title'), noTransition: true },
      component: () => import('./pages/VacationConfig.vue'),
    },
    {
      path: '/terrace-about',
      name: 'terraceAbout',
      meta: { title: 'Invoxia - ' + i18n.global.t('terrace_mode_title'), noTransition: true },
      component: () => import('./pages/TerraceAbout.vue'),
    },
    {
      path: '/start_track_app/',
      name: 'startTrackApp',
      meta: { title: 'Invoxia Tracker App' },
      component: () => import('./pages/StartTrackApp.vue'),
    },
    {
      path: '/start_sdc_app/',
      name: 'startSDCApp',
      meta: { title: 'Invoxia SDC App' },
      component: () => import('./pages/StartSDCApp.vue'),
    },
    // Takes param board_name
    {
      path: '/conflicting',
      name: 'productConflicting',
      meta: { title: 'Product Conflicting' },
      component: () => import('./pages/ProductConflicting.vue'),
    },
    {
      path: '/conflicting-unlink',
      name: 'productConflictingUnlink',
      meta: { title: 'Product Conflicting Unlink' },
      component: () => import('./pages/ProductConflictingUnlink.vue'),
    },
    {
      path: '/conflicting-security',
      name: 'productConflictingSecurity',
      meta: { title: 'Product Conflicting Security' },
      component: () => import('./pages/ProductConflictingSecurity.vue'),
    },
    {
      path: '/conflicting-serial',
      name: 'productConflictingSerial',
      meta: { title: 'Product Conflicting Serial' },
      component: () => import('./pages/ProductConflictingSerial.vue'),
    },
    {
      path: '/conflicting-fail',
      name: 'productConflictingFail',
      meta: { title: 'Product Conflicting Fail' },
      component: () => import('./pages/ProductConflictingSNfail.vue'),
    },
    {
      path: '/network_activate',
      name: 'Network Activate',
      meta: { title: 'Network activate' },
      component: () => import('./pages/NetworkActivate.vue'),
    },
    // GAU: add to /deep-link ?
    {
      path: '/install_restart',
      name: 'installRestart',
      meta: { title: 'Install Restart' },
      component: () => import('./pages/InstallRestart.vue'),
    },
    // GAU: deprecated ?
    {
      path: '/main',
      name: 'main',
      meta: {},
      component: () => import('./pages/Main.vue'),
    },
    // GAU: add to /deep-link ?
    {
      path: '/contact_us',
      name: 'contact us',
      meta: {},
      component: () => import('./pages/ContactUs.vue'),
    },
    // GAU: add to /deep-link ?
    {
      path: '/verify_auth',
      name: 'verify_auth',
      meta: {},
      component: () => import('./pages/VerifyAuth.vue'),
    },
    // GAU: add to /deep-link ?
    {
      path: '/install_quit',
      name: 'install quit',
      meta: {},
      component: () => import('./pages/InstallQuit.vue'),
    },
    // Public links are links that can be shared with others to track devices
    {
      path: '/app/public_links',
      name: 'publicLinks',
      meta: { title: 'Invoxia - public' },
      component: () => import('./pages/PublicLinks.vue'),
    },
    //
    {
      path: '/pub/trackers',
      name: 'publicLinksShared',
      meta: { title: 'Invoxia - public' },
      component: () => import('./pages/PublicLinksShow.vue'),
    },
    {
      path: '/app/theft_file',
      name: 'theftFile',
      meta: { title: 'Invoxia - theft' },
      component: () => import('./pages/TheftFile.vue'),
    },
    {
      path: '/error',
      name: 'error',
      meta: { title: 'Invoxia - ' + i18n.global.t('error') },
      component: () => import('./pages/Error.vue'),
    },
    {
      path: '/app/native/findmy',
      name: 'findMy',
      meta: {},
      component: () => import('./pages/NativeFindMy.vue'),
    },
    {
      path: '/app/native/rtb',
      name: 'realTimeBoost',
      meta: {},
      component: () => import('./pages/NativeBoost.vue'),
    },
    {
      path: '/app/native/:pathMatch(.*)*',
      alias: ['/exit'],
      name: 'unknownDeeplink',
      meta: { title: 'Invoxia - Needs Update' },
      component: () => import('./pages/AppNeedsUpdate.vue'),
    },
    // GAU: This page handles deep links and is served only in the web context.
    // Its purpose is to prevent redirection to Error404 when testing or clicking on deep links,
    // ensuring that no errors occurred during development.
    // Currently, only some deep-links are managed here; more will be added gradually.
    {
      path: '/deep-link',
      name: 'deepLink',
      meta: { title: 'Invoxia - Deep Link' },
      component: () => import('./pages/DeepLinkDefault.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      meta: { title: 'Invoxia - ' + i18n.global.t('error_404') },
      component: () => import('./pages/Error404.vue'),
    },
  ],
})

function setupNavigationGuards(router: Router) {
  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const store = useStore()

    // Route-specific data loading
    const name = to.name as string
    const loader = to.meta.guards as RouteGuard[]
    if (loader && loader.length > 0) {
      try {
        const results = await Promise.all(loader.map((fn) => fn(to, from, store, i18n)))
        return results.find((result) => result !== undefined)
      } catch (error) {
        console.error(`Failed to run navigation guard for ${name}:`, error)
        return { name: 'error' }
      }
    }
  })

  // Global webview middleware - adds webview query param without redirecting
  router.afterEach((to: RouteLocationNormalized) => {
    const store = useStore()
    const isWebView = store.getters['users/isWebView']

    // If in webview mode and the webview query param is not present, add it silently
    if (isWebView && !to.query.webview) {
      router.replace({
        ...to,
        query: {
          ...to.query,
          webview: 'true',
        },
      })
    }
  })
}

setupNavigationGuards(router)

export default router
